import { BroadviewClientError, type DashboardResponse } from "@broadview/api-client";
import { Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  BarChart3,
  Calculator,
  CircleDot,
  FileSearch,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { useState } from "react";
import { PortalAccountMenu } from "./PortalAccountMenu";
import { PortalDataError, PortalLoadingState } from "./PortalDataState";
import { formatMetricDisplayValue } from "@/lib/metric-display";
import { parseGovernedMetricPayload, useDashboardQuery } from "@/lib/portal-queries";
import { usePortalRuntime } from "@/lib/portal-runtime";

type IntelligenceView = "effectiveness-prevention" | "financial-intelligence";
type IntelligenceWidget = DashboardResponse["widgets"][number];

const financialClasses = [
  ["Actual", "Reconciled eligible transactions"],
  ["Accrued / Reserved", "Finance-approved accounting snapshot only"],
  ["Forecast", "Versioned forward-looking estimate"],
  ["Estimated Exposure", "Modelled consequence from declared assumptions"],
  ["Potential Avoided Exposure", "Scenario difference; never booked savings"],
  ["Awaiting Data / Not Quantified", "Required evidence is unavailable"],
] as const;

const assumptionLabels: Readonly<Record<string, string>> = {
  formula_version: "Formula version",
  horizon_months: "Horizon",
  delay_months: "Delay scenario",
  currency: "Currency",
  assumption_authority: "Assumption authority",
  finance_authority_role: "Finance authority",
  product_authority_role: "Product authority",
  capability_state: "Capability state",
  unavailable_reason: "Unavailable reason",
};

function title(view: IntelligenceView) {
  return view === "effectiveness-prevention" ? "Prevention Intelligence" : "Financial Intelligence";
}

function description(view: IntelligenceView) {
  return view === "effectiveness-prevention"
    ? "Governed effectiveness, read-across, and prevention evidence for accountable human review."
    : "Governed exposure and cost-of-delay scenarios with explicit financial classifications.";
}

function readable(value: unknown, key: string) {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value !== "string" && typeof value !== "number") return JSON.stringify(value);
  if (key === "horizon_months" || key === "delay_months") return `${value} months`;
  return String(value).replaceAll("_", " ");
}

export function GovernedIntelligenceDashboard({ view }: Readonly<{ view: IntelligenceView }>) {
  const { tenantContext } = usePortalRuntime();
  const dashboard = useDashboardQuery(view);
  const [selected, setSelected] = useState<{
    tenantId: string;
    widget: IntelligenceWidget;
  } | null>(null);
  const hasPermission = tenantContext.permissions.includes("metric.read");
  const selectedWidget =
    hasPermission &&
    dashboard.isSuccess &&
    selected !== null &&
    dashboard.data.tenantId === tenantContext.tenantId &&
    selected.tenantId === tenantContext.tenantId &&
    dashboard.data.widgets.includes(selected.widget)
      ? selected.widget
      : null;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1450px] items-center gap-4 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              {view === "effectiveness-prevention" ? (
                <Sparkles className="h-5 w-5" />
              ) : (
                <Calculator className="h-5 w-5" />
              )}
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">{title(view)}</div>
              <div className="text-xs text-muted-foreground">{tenantContext.tenantName}</div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Full dashboard
            </Link>
            <PortalAccountMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1450px] space-y-6 px-6 py-8">
        <section>
          <h1 className="text-2xl font-semibold tracking-tight">{title(view)}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{description(view)}</p>
        </section>

        <section className="rounded-xl border border-warning/40 bg-warning/10 p-5">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 h-5 w-5 text-warning" />
            <div>
              <h2 className="font-semibold">Governed demonstration</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Synthetic records use the production-shaped canonical, API, permission, and widget
                seams. Results are decision support only and prohibited from production use.
              </p>
            </div>
          </div>
        </section>

        {!hasPermission ? (
          <PortalDataError
            title={`${title(view)} access is not available`}
            error={
              new BroadviewClientError({
                status: 403,
                code: "FORBIDDEN",
                message: "The selected Division does not grant metric.read.",
              })
            }
            onRetry={() => undefined}
          />
        ) : dashboard.isPending ? (
          <PortalLoadingState label={`Loading ${title(view)}`} />
        ) : dashboard.isError ? (
          <PortalDataError error={dashboard.error} onRetry={() => void dashboard.refetch()} />
        ) : (
          <>
            <section className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-xs">
              <span className="inline-flex items-center gap-1.5 font-semibold text-warning">
                <CircleDot className="h-3 w-3" /> Synthetic · demonstration only
              </span>
              <span className="text-muted-foreground">
                Data as of {new Date(dashboard.data.dataAsOf).toLocaleString()}
              </span>
              <button
                type="button"
                onClick={() => void dashboard.refetch()}
                className="ml-auto inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 font-semibold hover:bg-muted"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Refresh
              </button>
            </section>

            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {dashboard.data.widgets.map((widget) => {
                const metric = parseGovernedMetricPayload(widget.payload);
                const disabled = metric?.dataState === "UNAVAILABLE";
                return (
                  <button
                    type="button"
                    key={widget.widgetId}
                    onClick={() => setSelected({ tenantId: tenantContext.tenantId, widget })}
                    className="rounded-xl border border-border p-5 text-left transition hover:border-primary/50"
                    style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <BarChart3 className="h-5 w-5 text-primary" />
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${disabled ? "bg-muted text-muted-foreground" : "bg-warning/10 text-warning"}`}
                      >
                        {disabled ? "DISABLED" : widget.dataState}
                      </span>
                    </div>
                    <div className="mt-4 text-xs text-muted-foreground">
                      {metric?.label ?? widget.widgetId}
                    </div>
                    <div className="mt-1 text-2xl font-semibold">
                      {metric ? formatMetricDisplayValue(metric) : "Awaiting Data"}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                      {metric?.financialClassification && (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 font-semibold text-primary">
                          {metric.financialClassification.replaceAll("_", " ")}
                        </span>
                      )}
                      <span className="rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                        {widget.quality} · {widget.reconciliation}
                      </span>
                    </div>
                    <div className="mt-3 text-xs text-muted-foreground">
                      View assumptions and provenance
                    </div>
                  </button>
                );
              })}
            </section>

            {view === "financial-intelligence" && (
              <section className="rounded-xl border border-border bg-card p-5">
                <h2 className="font-semibold">Financial classifications</h2>
                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {financialClasses.map(([classification, meaning]) => (
                    <div
                      key={classification}
                      className="rounded-lg border border-border bg-background p-3"
                    >
                      <div className="text-sm font-semibold">{classification}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{meaning}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {selectedWidget && (
        <EvidenceDrawer widget={selectedWidget} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function EvidenceDrawer({
  widget,
  onClose,
}: Readonly<{ widget: IntelligenceWidget; onClose: () => void }>) {
  const metric = parseGovernedMetricPayload(widget.payload);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-background/70 backdrop-blur-sm">
      <button type="button" className="flex-1" aria-label="Close evidence" onClick={onClose} />
      <aside className="h-full w-full max-w-xl overflow-y-auto border-l border-border bg-card p-6">
        <div className="flex items-start gap-3">
          <FileSearch className="mt-0.5 h-5 w-5 text-primary" />
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Assumptions and provenance
            </div>
            <h2 className="mt-1 text-lg font-semibold">{metric?.label ?? widget.widgetId}</h2>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="ml-auto rounded-md border border-border p-1.5"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {metric && (
          <dl className="mt-6 grid grid-cols-2 gap-3 text-sm">
            <Evidence label="Value" value={formatMetricDisplayValue(metric)} />
            <Evidence
              label="Definition"
              value={`${metric.definitionId} v${metric.definitionVersion}`}
            />
            <Evidence
              label="Classification"
              value={metric.financialClassification?.replaceAll("_", " ") ?? "Operational measure"}
            />
            <Evidence label="Data state" value={metric.dataState} />
            <Evidence label="Quality" value={metric.quality} />
            <Evidence label="Reconciliation" value={metric.reconciliation} />
          </dl>
        )}

        <section className="mt-6 rounded-lg border border-border bg-background p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Declared assumptions
          </h3>
          <dl className="mt-3 space-y-3 text-sm">
            {metric &&
              Object.entries(metric.dimensions)
                .filter(([key]) => key in assumptionLabels)
                .map(([key, value]) => (
                  <div key={key} className="flex items-start justify-between gap-4">
                    <dt className="text-muted-foreground">{assumptionLabels[key]}</dt>
                    <dd className="text-right font-medium">{readable(value, key)}</dd>
                  </div>
                ))}
          </dl>
        </section>

        <section className="mt-4 rounded-lg border border-border bg-background p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Source and lineage
          </h3>
          <p className="mt-2 text-sm">{widget.sourceSummary}</p>
          <p className="mt-2 text-xs text-muted-foreground">
            {widget.drillThroughToken ??
              metric?.lineageReference ??
              "No lineage reference supplied"}
          </p>
        </section>
      </aside>
    </div>
  );
}

function Evidence({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 font-semibold">{value}</dd>
    </div>
  );
}
